import { DSBadge } from "@components/design-system/molecules";
import { faBone } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTreatBalance } from "@hooks/use-treat-balance";

const LOW_BALANCE_THRESHOLD = 100;

function variantFor(total: number) {
  if (total <= 0) return "red" as const;
  if (total < LOW_BALANCE_THRESHOLD) return "yellow" as const;
  return "green" as const;
}

export interface TreatBalanceIndicatorProps {
  onClick?: () => void;
}

export const TreatBalanceIndicator = ({
  onClick,
}: TreatBalanceIndicatorProps) => {
  const { balance } = useTreatBalance();

  if (!balance) return null;

  const tooltip = [
    `Total: ${balance.totalBalance} treats`,
    `Monthly: ${balance.monthlyBalance}`,
    `Permanent: ${balance.permanentBalance}`,
  ].join("\n");
  const ariaLabel = `Treat balance: ${balance.totalBalance} treats. Monthly: ${balance.monthlyBalance}. Permanent: ${balance.permanentBalance}.`;

  const badge = (
    <DSBadge
      variant={variantFor(balance.totalBalance)}
      size="sm"
      dataTestId="treat-balance-indicator"
    >
      <span className="inline-flex items-center">
        <FontAwesomeIcon icon={faBone} />
        <span className="inline-block max-w-0 overflow-hidden whitespace-nowrap transition-all duration-200 ease-out group-hover:ml-1 group-hover:max-w-32 group-focus-visible:ml-1 group-focus-visible:max-w-32">
          {balance.totalBalance.toLocaleString()} treats
        </span>
      </span>
    </DSBadge>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={tooltip}
        aria-label={`${ariaLabel} Click to view billing.`}
        className="group cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        data-testid="treat-balance-indicator-button"
      >
        {badge}
      </button>
    );
  }

  return (
    <div
      title={tooltip}
      aria-label={ariaLabel}
      role="status"
      className="group"
    >
      {badge}
    </div>
  );
};

TreatBalanceIndicator.displayName = "TreatBalanceIndicator";
